import {Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output, signal, ChangeDetectionStrategy} from '@angular/core';
import {
    AzureGeocodingService,
    CompanyTypeService,
    LoadingSpinnerService,
    ProjectAttributeService,
    ProjectDetailsDraftService,
    ProjectFileService,
    ProjectService,
    ToastService,
    UserService
} from '../../services';
import {IProjectDetailsDraft} from '../../services/project-details-draft/project-details-draft.service';
import {ProjectAttribute, ProjectAttributes} from '../../models/project/project-attribute';
import {TermsAndConditions} from '../../enums/terms-and-conditions/terms-and-conditions';
import {TranslateService} from '@ngx-translate/core';
import {ContractType} from '../../enums/contract-type/contract-type';
import {ProjectDetails} from '../../models';
import moment from 'moment';
import {FormatEuroCentsDirective} from '../../directives/format-euro-cents.directive';
import {UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {forkJoin, interval, merge, Observable, of, shareReplay, startWith, Subscription, switchMap} from 'rxjs';
import {ICompanyType} from '../../models/user/user';
import {DateUtil, findTenderCostsRange, TENDER_COSTS_RANGES} from '../../utils';
import {ProjectImage} from '../../models/project/project-image';
import {DomSanitizer, SafeResourceUrl} from "@angular/platform-browser";
import {debounceTime, filter, map} from "rxjs/operators";
import {createIntegerMask} from '../../utils/question-answer.util';

/**
 * A single slide of the impression photo carousel. The project's thumbnail (shown e.g. in
 * the project list) is simply whichever photo is first — there's no separate "primary" flag.
 *
 * <p>`id` is undefined for the pinned thumbnail slide, which isn't a real gallery
 * `ProjectImage` and therefore can't be deleted through `ProjectFileService`.
 */
interface CarouselPhoto {
    id: number | undefined;
    src: SafeResourceUrl;
    // Plain-string form of `src`, kept alongside it because SafeResourceUrl is an opaque
    // wrapper (from DomSanitizer) that can't be safely read back out as a string.
    dataUrl: string;
}

/**
 * Page component for displaying details of a PRM 2.0 project.
 */
@Component({
    selector: 'app-project-analysis-details',
    templateUrl: './project-analysis-details.component.html',
    styleUrls: ['./project-analysis-details.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ProjectAnalysisDetailsComponent implements OnInit, OnDestroy {
    protected readonly numberOfUnitsMask = createIntegerMask(0);
    private projectService = inject(ProjectService);
    private companyTypeService = inject(CompanyTypeService);
    private userService = inject(UserService);
    private translateService = inject(TranslateService);
    private toastService = inject(ToastService);
    private projectAttributeService = inject(ProjectAttributeService);
    public loadingSpinnerService = inject(LoadingSpinnerService);
    private projectFileService = inject(ProjectFileService);
    private readonly sanitizer = inject(DomSanitizer);
    private azureGeocodingService = inject(AzureGeocodingService);
    private projectDetailsDraftService = inject(ProjectDetailsDraftService);

    /** Top-level ProjectDetails fields editable on this page (excludes rich class-instance fields like quickscanAnswers/projectLibraryItems, edited by other pages). */
    private static readonly DRAFT_FIELDS = ['name', 'address', 'city', 'description', 'company', 'newOwner', 'newRiskManager'] as const;
    private static readonly DRAFT_PERSIST_INTERVAL_MS = 2_000;

    private lastPersistedDraft: string | null = null;

    @Input()
    set projectDetails(val: ProjectDetails) {
        this.projectDetailsValue = val;
        this.restoreDraftIfPresent();
        this.updateDerivedFields();
        if (val) {
            this.setProjectDetails(val);
            this.checkCanEditOwner();
        }

        this.loadingSpinnerService.hide();
    }

    @Output() isFormValid = new EventEmitter<boolean>();
    @Output() projectDetailsChanged = new EventEmitter<ProjectDetails>();
    public projectPhotos = signal<CarouselPhoto[]>([]);
    public isProcessingThumbnail = false;

    public projectDetailsForm: UntypedFormGroup;
    private subscriptions: Subscription[] = [];

    public projectDetailsValue: ProjectDetails = null;

    public companyType: ICompanyType = null;
    public ownerChangeable = false;
    public riskManagerChangeable = false;
    public companyNames?: string[];
    public isApiProject = false;
    public contractTypes: { key: string; value: string }[];
    public termsAndConditions: { key: string; value: string }[];

    public readonly projectAttributes$: Observable<ProjectAttributes> = this.projectAttributeService.getAttributes().pipe(
        startWith({
            workTypes: [],
            buildingClassifications: [],
            buildingSubClassifications: []
        }),
        shareReplay(1)
    );

    public activeLocationImageIndex = 0;
    public contractType: string;
    public termsAndCondition: string;
    public constructionCosts: string;
    public readonly tenderCostsRanges = TENDER_COSTS_RANGES;
    public profitContribution: string;
    private maxDescriptionLength = 1024;
    private maxContractTypeLength = 255;
    private maxTermsAndConditionsLength = 255;
    private maxClientLength = 255;

    public costsForm: UntypedFormGroup;

    get durationInMonths(): string {
        return this.projectDetailsValue.projectInfo.durationInMonths ? '' + this.projectDetailsValue.projectInfo.durationInMonths : '';
    }

    set startDate(startDate: string) {
        this.projectDetailsValue.projectInfo.workStartsAt = moment(startDate).format('YYYY-MM-DD');
        this.projectDetailsValue.projectInfo.durationInMonths = DateUtil.diffInMonths(this.projectDetailsValue.projectInfo.workStartsAt, this.projectDetailsValue.projectInfo.workEndsAt);
    }

    get startDate(): string {
        return this.projectDetailsValue.projectInfo.workStartsAt;
    }

    set endDate(endDate: string) {
        this.projectDetailsValue.projectInfo.workEndsAt = moment(endDate).format('YYYY-MM-DD');
        this.projectDetailsValue.projectInfo.durationInMonths = DateUtil.diffInMonths(this.projectDetailsValue.projectInfo.workStartsAt, this.projectDetailsValue.projectInfo.workEndsAt);
    }

    get endDate(): string {
        return this.projectDetailsValue.projectInfo.workEndsAt;
    }

    get tenderCostsRange(): number | null {
        return findTenderCostsRange(this.projectDetailsValue.projectInfo.tenderCosts)?.value ?? null;
    }

    set tenderCostsRange(value: number | null) {
        // keep a legacy amount as is while its range stays selected
        if (value !== this.tenderCostsRange) {
            this.projectDetailsValue.projectInfo.tenderCosts = value;
        }
    }

    get projectVersionLabel(): string | null {
        const version = this.projectDetailsValue?.projectVersion;
        return version ? `v${version.majorVersion}.${version.minorVersion}` : null;
    }

    private toDataUrl(image: ProjectImage): string {
        return image.imageBase64.startsWith('data:')
            ? image.imageBase64
            : `data:${image.contentType ?? 'image/jpeg'};base64,${image.imageBase64}`;
    }

    private toCarouselPhoto(image: ProjectImage): CarouselPhoto {
        const dataUrl = this.toDataUrl(image);
        return {
            id: image.id,
            src: this.sanitizer.bypassSecurityTrustResourceUrl(dataUrl),
            dataUrl
        };
    }

    ngOnInit(): void {
        if (!this.projectDetailsValue) {
            this.loadingSpinnerService.show();
        }

        this.checkCanEditOwner();

        const termsAndConditions = [];
        for (const key in TermsAndConditions) {
            if (Object.prototype.hasOwnProperty.call(TermsAndConditions, key)) {
                const value: string = TermsAndConditions[key];
                termsAndConditions.push({
                    key: value,
                    value: this.translateService.instant(`PAGE_PROJECT_DETAILS.termsAndConditions.${value}`)
                });
            }
        }
        this.termsAndConditions = termsAndConditions;
        this.setCostsForm();
        if (this.projectDetailsValue) {
            this.loadProjectPhotos();
            this.wireDraftPersistence();
        }
    }

    private wireDraftPersistence(): void {
        this.subscriptions.push(
            interval(ProjectAnalysisDetailsComponent.DRAFT_PERSIST_INTERVAL_MS)
                .pipe(filter(() => this.canEditDetails()))
                .subscribe(() => this.persistDraftIfChanged())
        );
    }

    private persistDraftIfChanged(): void {
        const snapshot = this.buildDraftSnapshot();
        const serialized = JSON.stringify(snapshot);
        if (serialized === this.lastPersistedDraft) {
            return;
        }

        this.lastPersistedDraft = serialized;
        this.projectDetailsDraftService.save(this.projectDetailsValue.id, snapshot);
    }

    /**
     * projectInfo is taken as a whole: unlike ProjectDetails itself, it's a flat bag of
     * primitives/string-arrays with no class-instance fields (toApiModel() passes it
     * through untouched), so there's no risk of a stale/foreign shape landing on it —
     * and no whitelist to forget to update when a new projectInfo field is added.
     */
    private buildDraftSnapshot(): IProjectDetailsDraft {
        return {
            ...ProjectAnalysisDetailsComponent.pick(this.projectDetailsValue, ProjectAnalysisDetailsComponent.DRAFT_FIELDS),
            projectInfo: {...this.projectDetailsValue.projectInfo}
        };
    }

    private restoreDraftIfPresent(): void {
        if (!this.projectDetailsValue) {
            return;
        }

        const draft = this.projectDetailsDraftService.load(this.projectDetailsValue.id);
        if (!draft) {
            return;
        }

        this.projectDetailsDraftService.clear(this.projectDetailsValue.id);

        const currentSnapshot = JSON.stringify(this.buildDraftSnapshot());
        const draftSnapshot = JSON.stringify({
            ...ProjectAnalysisDetailsComponent.pick(draft, ProjectAnalysisDetailsComponent.DRAFT_FIELDS),
            projectInfo: {...draft.projectInfo}
        });
        if (currentSnapshot === draftSnapshot) {
            return;
        }

        Object.assign(this.projectDetailsValue, ProjectAnalysisDetailsComponent.pick(draft, ProjectAnalysisDetailsComponent.DRAFT_FIELDS));
        Object.assign(this.projectDetailsValue.projectInfo, draft.projectInfo);

        this.projectService.saveProjectDetails(this.projectDetailsValue).finally();
    }

    private static pick<T extends object>(source: T, keys: readonly (keyof T)[]): Partial<T> {
        return keys.reduce((acc, key) => ({...acc, [key]: source[key]}), {} as Partial<T>);
    }

    private loadProjectPhotos(): void {
        this.subscriptions.push(
            this.projectFileService.listImages(this.projectDetailsValue.id).pipe(
                map((images) => images.filter(image => !image.isPrimary)),
                switchMap(images => {
                    const imagesWithId = images.filter(image => image?.id);
                    // forkJoin on an empty array completes without emitting, which would
                    // leave projectPhotos stuck at its initial value when there's no gallery yet
                    return imagesWithId.length
                        ? forkJoin(imagesWithId.map(image => this.projectFileService.getImage(image.id, this.projectDetailsValue.id, false)))
                        : of([]);
                })
            ).subscribe(images => {
                this.projectPhotos.set(images.map(image => this.toCarouselPhoto(image)));
            })
        );
    }

    ngOnDestroy(): void {
        this.clearCostsForm();

        // This component is destroyed/recreated on every step switch (see the parent's
        // `@if (stepId === projectSteps.DETAILS)`). Its last-emitted validity would
        // otherwise stick on the parent forever once invalid, silently blocking
        // flushProjectSave() on every later step change (PRM-156).
        this.isFormValid.emit(true);
    }

    private static readonly EURO_FIELDS = ['constructionCosts', 'profitContribution'] as const;

    private setCostsForm(): void {
        this.costsForm = new UntypedFormGroup({
            constructionCosts: new UntypedFormControl(this.constructionCosts),
            profitContribution: new UntypedFormControl(this.profitContribution)
        });

        ProjectAnalysisDetailsComponent.EURO_FIELDS.forEach((field) => this.registerEuroField(field));
    }

    private registerEuroField(field: typeof ProjectAnalysisDetailsComponent.EURO_FIELDS[number]): void {
        const control = this.costsForm.get(field);

        this.subscriptions.push(control.valueChanges.subscribe((value) => {
            const numberValue = FormatEuroCentsDirective.convertToNumber(value);
            this[field] = FormatEuroCentsDirective.convertNumberToEuros(numberValue, this.translateService.getFallbackLang());

            control.setValue(this[field], {emitEvent: false});

            this.updateMoneyValues();
        }));
    }

    private clearCostsForm() {
        this.subscriptions.forEach((subscription) => {
            subscription.unsubscribe();
        });
        this.subscriptions = [];
        this.costsForm.removeControl('constructionCosts');
        this.costsForm.removeControl('profitContribution');
        this.costsForm = undefined;
    }

    public isProjectOwner() {
        return this.projectService.isOwner(this.projectDetailsValue);
    }

    public canEditDetails() {
        return this.projectDetailsValue
            && !this.projectDetailsValue.isEditLocked()
            && this.projectService.canEditProjectDetails(this.projectDetailsValue);
    }

    public checkCanEditOwner() {
        if (this.projectDetailsForm) {
            this.ownerChangeable = this.projectService.canManageCompany(this.projectDetailsValue) && !this.projectDetailsValue.isClosed();
            this.riskManagerChangeable = (this.ownerChangeable || this.projectService.isOwner(this.projectDetailsValue)) && !this.projectDetailsValue.isClosed();
            if (this.ownerChangeable) {
                this.projectDetailsForm.get('newOwner').enable();
            } else {
                this.projectDetailsForm.get('newOwner').disable();
            }
            if (this.riskManagerChangeable) {
                this.projectDetailsForm.get('newRiskManager').enable();
            } else {
                this.projectDetailsForm.get('newRiskManager').disable();
            }
        }
    }

    /**
     * Uploads a file as a project photo. There's no separate "primary" upload — the
     * project's thumbnail (still read as `thumbnailBase64`/`projectInfo.imageBase64` by e.g.
     * the project list) is simply whichever photo ends up first, set once when the gallery
     * goes from empty to having its first photo.
     */
    private uploadProjectPhoto(file: File): void {
        // If the file is >100MB in bytes (100.000.000) warn that upload will fail
        if (file.size >= 100000000) {
            this.toastService.open({
                type: 'error',
                duration: 4500,
                icon: 'exclamation',
                code: '0',
                message: this.translateService.instant('ERRORS.filesize_too_high'),
                dismiss: true
            });
            return;
        }

        this.isProcessingThumbnail = true;

        const formData = new FormData();
        formData.append('file', file);

        this.projectFileService.uploadImage(this.projectDetailsValue.id, formData, false).pipe(
            switchMap(image => this.projectFileService.getImage(image.id, this.projectDetailsValue.id, false))
        ).subscribe({
            next: image => {
                this.projectPhotos.update(photos => [...photos, this.toCarouselPhoto(image)]);
                this.activeLocationImageIndex = this.projectPhotos().length - 1;
                this.isProcessingThumbnail = false;

                // re-assert from the current gallery on every change, rather than only
                // the first upload, so the thumbnail can never drift out of sync
                this.syncThumbnailFromGallery();
            },
            error: () => {
                // error toast already shown by ProjectFileService
                this.isProcessingThumbnail = false;
            }
        });
    }

    /**
     * Keeps the legacy single-image fields in sync with the project's thumbnail (always
     * whichever photo is currently first), so other screens that still read them (e.g. the
     * project list thumbnail) keep working.
     */
    private syncThumbnailFromGallery(): void {
        const thumbnail = this.projectPhotos()[0];

        // Per the backend contract (see ProjectService#update): thumbnailBase64 null means
        // "leave as is", so an explicit clear must send '' instead.
        this.projectDetailsValue.projectInfo.imageBase64 = thumbnail?.dataUrl ?? null;
        this.projectDetailsValue.thumbnailBase64 = thumbnail?.dataUrl ?? '';

        // keep the sticky header's project reference in sync, so its own "Opslaan" save
        // doesn't overwrite the thumbnail with a stale copy that predates this change
        this.projectDetailsChanged.emit(this.projectDetailsValue);

        // persist right away: the pinned thumbnail isn't part of the page's regular
        // form-field editing that's saved together via its own Save action
        this.projectService.saveProjectDetails(this.projectDetailsValue).catch(() => {
            // error toast already shown by ProjectService
        });
    }

    public onAddLocationImages(event: Event): void {
        const input = event.target as HTMLInputElement;
        const files = input.files ? Array.from(input.files) : [];

        // the backend accepts one image per upload, so each selected file is its own request
        files.forEach(file => this.uploadProjectPhoto(file));

        input.value = '';
    }

    public removeLocationImage(index: number, id: number | undefined): void {
        if (!id) {
            // legacy pinned-thumbnail data that predates the photo gallery, with no matching ProjectImage row
            this.removePhotoAt(index);
            this.syncThumbnailFromGallery();
            return;
        }

        this.projectFileService.deleteImage(id, this.projectDetailsValue.id).subscribe(() => {
            this.removePhotoAt(index);
            this.syncThumbnailFromGallery();
        });
    }

    private removePhotoAt(index: number): void {
        const result = this.projectPhotos().filter((carouselPhoto, i) => i !== index);
        this.projectPhotos.set(result);

        if (this.activeLocationImageIndex >= this.projectPhotos().length) {
            this.activeLocationImageIndex = Math.max(0, this.projectPhotos().length - 1);
        }
    }

    public setActiveLocationImage(index: number): void {
        this.activeLocationImageIndex = index;
    }


    private lastAvailableSubClassificationsKey: string = null;
    private lastAvailableSubClassificationsResult: ProjectAttribute[] = [];

    public availableBuildingSubClassifications(attributes: ProjectAttributes): ProjectAttribute[] {
        const selectedClassificationIds = this.projectDetailsValue.projectInfo.buildingClassifications ?? [];
        const selectedSubClassificationIds = this.projectDetailsValue.projectInfo.buildingSubClassifications ?? [];
        const cacheKey = `${attributes.buildingSubClassifications.length}|${selectedClassificationIds.join(',')}|${selectedSubClassificationIds.join(',')}`;

        if (cacheKey === this.lastAvailableSubClassificationsKey) {
            return this.lastAvailableSubClassificationsResult;
        }

        const selectedClassificationIdSet = new Set(selectedClassificationIds);
        const selectedSubClassificationIdSet = new Set(selectedSubClassificationIds);

        this.lastAvailableSubClassificationsKey = cacheKey;
        this.lastAvailableSubClassificationsResult = attributes.buildingSubClassifications.filter((subClassification) =>
            selectedSubClassificationIdSet.has(subClassification.id)
            || (subClassification.parents ?? []).some((parentId) => selectedClassificationIdSet.has(parentId))
        );

        return this.lastAvailableSubClassificationsResult;
    }

    public showPreviousLocationImage(): void {
        const count = this.projectPhotos().length;
        this.activeLocationImageIndex = (this.activeLocationImageIndex - 1 + count) % count;
    }

    public showNextLocationImage(): void {
        const count = this.projectPhotos().length;
        this.activeLocationImageIndex = (this.activeLocationImageIndex + 1) % count;
    }

    public isContractTypeOther(): boolean {
        return this.getContractType() === ContractType.OTHER;
    }

    public getContractType() {
        if (this.projectDetailsValue) {
            if (this.projectDetailsValue.projectInfo.contractType === null) {
                return null;
            }
            const foundType = this.contractTypes.find((ct) => ct.key === this.projectDetailsValue.projectInfo.contractType);

            return foundType !== undefined ? foundType.key : ContractType.OTHER;
        }
        return undefined;
    }

    public getTermsAndConditions() {
        if (this.projectDetailsValue) {
            return this.getType(this.projectDetailsValue.projectInfo.termsAndConditions, TermsAndConditions, TermsAndConditions.OTHER);
        }
        return undefined;
    }

    public isTermsAndConditionsOther(): boolean {
        return this.getTermsAndConditions() === TermsAndConditions.OTHER;
    }

    public updateTermsAndConditions() {
        this.projectDetailsValue.projectInfo.termsAndConditions = this.termsAndCondition;

        if (this.getTermsAndConditions() === TermsAndConditions.OTHER && this.termsAndCondition === TermsAndConditions.OTHER) {
            this.projectDetailsValue.projectInfo.termsAndConditions = '';
        }
    }

    public updateContractType() {
        this.projectDetailsValue.projectInfo.contractType = this.contractType;

        if (this.getContractType() === ContractType.OTHER && this.contractType === ContractType.OTHER) {
            this.projectDetailsValue.projectInfo.contractType = '';
        }
    }

    public stringLimitReached(property: string) {
        const projectInfo = this.projectDetailsValue.projectInfo;

        const descriptionLimitReached = this.maxDescriptionLength - this.getStringLength(this.projectDetailsForm.controls.description.value) < 0;
        const termsAndConditionsLimitReached = this.maxTermsAndConditionsLength - this.getStringLength(this.projectDetailsValue.projectInfo.termsAndConditions) < 0;
        const contractTypeLimitReached = this.maxContractTypeLength - this.getStringLength(this.projectDetailsValue.projectInfo.contractType) < 0;
        const clientLimitReached = this.maxClientLength - this.getStringLength(projectInfo.client) < 0;

        this.isFormValid.emit(
            !(descriptionLimitReached || termsAndConditionsLimitReached || contractTypeLimitReached || clientLimitReached)
            && this.projectDetailsForm.valid);
        switch (property) {
            case 'description':
                return descriptionLimitReached;
            case 'termsAndConditionsOther':
                return termsAndConditionsLimitReached;
            case 'client':
                return clientLimitReached;
            case 'contractTypeOther':
                return contractTypeLimitReached;
        }
    }

    private getStringLength(value): number {
        return value ? value.length : 0;
    }

    private setProjectDetails(projectDetails: ProjectDetails) {
        const disabled = !this.canEditDetails();
        this.projectDetailsForm = new UntypedFormGroup({
            description: new UntypedFormControl({
                value: projectDetails.description ? projectDetails.description : '',
                disabled
            }, Validators.maxLength(this.maxDescriptionLength)),
            name: new UntypedFormControl({
                value: projectDetails.name || '',
                disabled
            }, [Validators.maxLength(255), Validators.required]),
            address: new UntypedFormControl({
                value: projectDetails.address || '',
                disabled
            }, [Validators.maxLength(255), Validators.required]),
            city: new UntypedFormControl({
                value: projectDetails.city || '',
                disabled
            }, [Validators.maxLength(255), Validators.required]),
            newOwner: new UntypedFormControl({
                value: projectDetails.newOwner || null,
                disabled: disabled || !this.ownerChangeable
            }, Validators.required),
            newRiskManager: new UntypedFormControl({
                value: projectDetails.newRiskManager || null,
                disabled: disabled || !this.ownerChangeable
            }),
            company: new UntypedFormControl({
                value: projectDetails.company || '',
                disabled: disabled || !this.isProjectOwner() || this.isApiProject
            }),
            projectType: new UntypedFormControl({value: projectDetails.projectType?.name || '', disabled: true}),
        });

        this.companyTypeService.getCompanyType(this.projectDetailsValue.companyTypeId).then((companyType) => {
            this.companyType = companyType;
        });

        this.subscriptions.push(
            this.projectDetailsForm.statusChanges.subscribe((value) => {
                if (!this.isProcessingThumbnail) {
                    this.isFormValid.emit(value === 'VALID');
                }
            }),
            this.projectDetailsForm.valueChanges
                .subscribe((values) => {
                    this.isFormValid.emit(this.projectDetailsForm.valid);
                    Object.keys(values).forEach((key) => {
                        if (this.projectDetailsForm.get(key).valid) { // Save everything that is valid
                            this.projectDetailsValue[key] = values[key];
                        }
                    });
                })
        );

        this.wireLocationSync();
    }

    /**
     * Keeps the map pin in sync with the address fields: geocodes on address/city edits,
     * and backfills the pin once on load if the project has no coordinates yet. Country
     * isn't editable here (it's set once at project creation) — its current model value
     * is still sent along to Azure Maps for a more accurate lookup.
     */
    private wireLocationSync(): void {
        const address = this.projectDetailsForm.get('address');
        const city = this.projectDetailsForm.get('city');

        this.subscriptions.push(
            merge(address.valueChanges, city.valueChanges).pipe(
                debounceTime(600),
                filter(() => address.valid && city.valid),
                switchMap(() => this.azureGeocodingService.geocodeAddress(address.value, city.value, this.projectDetailsValue.country))
            ).subscribe((coordinates) => this.applyGeocodedLocation(coordinates))
        );

        if (!this.projectDetailsValue.projectInfo.location && address.valid && city.valid) {
            this.subscriptions.push(
                this.azureGeocodingService.geocodeAddress(address.value, city.value, this.projectDetailsValue.country)
                    .subscribe((coordinates) => this.applyGeocodedLocation(coordinates))
            );
        }
    }

    private applyGeocodedLocation(coordinates: [number, number] | null): void {
        if (!coordinates) {
            return;
        }

        const [latitude, longitude] = coordinates;
        this.projectDetailsValue.projectInfo.location = ProjectAnalysisDetailsComponent.formatLocation(latitude, longitude);
    }

    private static readonly LOCATION_DECIMALS = 4;

    private static formatLocation(latitude: number, longitude: number): string {
        return `${latitude.toFixed(ProjectAnalysisDetailsComponent.LOCATION_DECIMALS)}, ${longitude.toFixed(ProjectAnalysisDetailsComponent.LOCATION_DECIMALS)}`;
    }

    /**
     * Handles a click on the location map: reverse-geocodes the clicked point and writes
     * the resulting address/city/coordinates back onto the form and the project.
     */
    public onPinMoved([latitude, longitude]: [number, number]): void {
        this.subscriptions.push(
            this.azureGeocodingService.reverseGeocode(latitude, longitude).subscribe((result) => {
                if (!result) {
                    return;
                }

                this.projectDetailsForm.patchValue({address: result.address, city: result.city}, {emitEvent: false});
                this.projectDetailsValue.address = result.address;
                this.projectDetailsValue.city = result.city;
                this.projectDetailsValue.projectInfo.location = ProjectAnalysisDetailsComponent.formatLocation(latitude, longitude);
            })
        );
    }

    private addProjectCompany(): void {
        if (this.projectDetailsValue) {
            if (!this.companyNames.includes(this.projectDetailsValue.company)) {
                this.companyNames.push(this.projectDetailsValue.company);
                this.isApiProject = true;
                this.projectDetailsForm.get('company').disable();
            }
        }
    }

    private getType(value: string, type, defaultType) {
        if (value === null) {
            return null;
        }
        for (const aType in type) {
            if (type[aType] === value) {
                return value;
            }
        }

        return defaultType;
    }

    private updateDerivedFields(): void {
        if (this.projectDetailsValue) {
            this.initializeContractTypes();

            this.termsAndCondition = this.getTermsAndConditions();
            this.contractType = this.getContractType();

            this.profitContribution = FormatEuroCentsDirective.convertNumberToEuros(this.projectDetailsValue.projectInfo.profitContribution, 'nl');
            this.constructionCosts = FormatEuroCentsDirective.convertNumberToEuros(this.projectDetailsValue.projectInfo.constructionCosts, 'nl');
            this.setCostsForm();

            this.projectDetailsValue.projectInfo.workStartsAt = this.startDate ? moment(this.startDate).format('YYYY-MM-DD') : null;
            this.projectDetailsValue.projectInfo.workEndsAt = this.endDate ? moment(this.endDate).format('YYYY-MM-DD') : null;
            this.projectDetailsValue.projectInfo.durationInMonths = DateUtil.diffInMonths(this.projectDetailsValue.projectInfo.workStartsAt, this.projectDetailsValue.projectInfo.workEndsAt);

            this.userService.getUserInfo().then((userInfo) => {
                this.companyNames = userInfo.companiesForManuallyAddingProjects
                    .filter((entry) => entry.companyType.id === this.projectDetailsValue.companyTypeId)
                    .map((entry) => entry.name);

                this.addProjectCompany();
            });
        }
    }

    private updateMoneyValues(): void {
        if (FormatEuroCentsDirective.convertToNumber(this.profitContribution) > 999000000000000 || FormatEuroCentsDirective.convertToNumber(this.constructionCosts) > 999000000000000) {
            this.toastService.open({
                type: 'error',
                duration: 1500,
                icon: 'exclamation',
                code: '0',
                message: this.translateService.instant('ERRORS.too_many_euros'),
                dismiss: true
            });
        } else {
            this.projectDetailsValue.projectInfo.profitContribution = this.profitContribution ? FormatEuroCentsDirective.convertToNumber(this.profitContribution) : null;
            this.projectDetailsValue.projectInfo.constructionCosts = this.constructionCosts ? FormatEuroCentsDirective.convertToNumber(this.constructionCosts) : null;
        }
    }

    private getTranslationWithFallback(keyPrefix: string, value: string): string {
        const translationKey = `${keyPrefix}.${value}`.trim();
        const translation = this.translateService.instant(translationKey);

        return translation === translationKey ? value : translation;
    }

    private initializeContractTypes(): void {
        const contractTypes = this.projectDetailsValue.projectInfo.contractTypeLabels
            .map((label) => ({
                key: label,
                value: this.getTranslationWithFallback(`PAGE_PROJECT_DETAILS.contractType`, label)
            }));

        contractTypes.push({
            key: ContractType.OTHER,
            value: this.translateService.instant(`PAGE_PROJECT_DETAILS.contractType.other`)
        });

        this.contractTypes = contractTypes;
    }
}
